import { TypographyStyle, ColorConfig } from "./types";

/**
 * Viewport range for fluid typography
 */
const MIN_VIEWPORT = 320; // px
const MAX_VIEWPORT = 1920; // px

/**
 * Creates a clamp() CSS value for fluid typography using px and vw
 * Simpler format: clamp(MINpx, VWvw, MAXpx)
 * The vw value is calculated to scale proportionally between viewports
 */
function createClamp(minPx: number, maxPx: number): string {
  // Calculate the vw value that scales proportionally
  // Formula: ((max - min) / (maxViewport - minViewport)) * 100
  const vwValue = ((maxPx - minPx) / (MAX_VIEWPORT - MIN_VIEWPORT)) * 100;

  // Calculate base offset to ensure we hit minPx at MIN_VIEWPORT
  const baseOffset = minPx - (MIN_VIEWPORT * vwValue) / 100;

  // Return in format: clamp(min, calc(base + vw), max)
  return `clamp(${minPx}px, calc(${baseOffset.toFixed(2)}px + ${vwValue.toFixed(
    2
  )}vw), ${maxPx}px)`;
}

/**
 * Perfect Fourth scale (1.333 ratio) from base 16px → 18px
 *
 * Calculations:
 * Base: 16px → 18px
 * Scale up by 1.333x for each heading level
 */

// ==================== HEADING STYLES ====================

// Big Shoulders Display: condensed poster type. Weights on Google Fonts:
// 400, 700, 900. Because it's condensed, same pixel size takes ~70% of the
// horizontal room of a normal-proportion sans, so mobile floors are higher
// than typical (H1 42, H2 32) to hold hierarchy against a 16px body.
// Letter-spacing opens up at smaller sizes so small caps don't collide.
export const H1_STYLE: TypographyStyle = {
  fontSize: createClamp(42, 72),
  lineHeight: 1.05,
  letterSpacing: "-0.01em",
  fontWeight: 900,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

export const H2_STYLE: TypographyStyle = {
  fontSize: createClamp(36, 52),
  lineHeight: 1.1,
  letterSpacing: "0em",
  fontWeight: 800,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

export const H3_STYLE: TypographyStyle = {
  fontSize: createClamp(28, 40),
  lineHeight: 1.2,
  letterSpacing: "0em",
  fontWeight: 700,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

export const H4_STYLE: TypographyStyle = {
  fontSize: createClamp(20, 28),
  lineHeight: 1.25,
  letterSpacing: "0.005em",
  fontWeight: 700,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

export const H5_STYLE: TypographyStyle = {
  fontSize: createClamp(18, 22),
  lineHeight: 1.35,
  letterSpacing: "0.01em",
  fontWeight: 700,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

export const H6_STYLE: TypographyStyle = {
  fontSize: createClamp(16, 18),
  lineHeight: 1.4,
  letterSpacing: "0.02em",
  fontWeight: 700,
  fontFamily: "'Big Shoulders Display', sans-serif",
};

// ==================== TEXT STYLES ====================

export const TEXT_STYLES = {
  xs: {
    fontSize: createClamp(10, 12),
    lineHeight: 1.6,
    letterSpacing: "0em",
    fontWeight: 400,
    fontFamily: "'Public Sans', sans-serif",
  },
  sm: {
    fontSize: createClamp(12, 14),
    lineHeight: 1.6,
    letterSpacing: "0em",
    fontWeight: 400,
    fontFamily: "'Public Sans', sans-serif",
  },
  md: {
    fontSize: createClamp(14, 16), // Base/default — matches Dictionary body text
    lineHeight: 1.6,
    letterSpacing: "0em",
    fontWeight: 400,
    fontFamily: "'Public Sans', sans-serif",
  },
  lg: {
    fontSize: createClamp(16, 18),
    lineHeight: 1.6,
    letterSpacing: "0em",
    fontWeight: 400,
    fontFamily: "'Public Sans', sans-serif",
  },
  xl: {
    fontSize: createClamp(18, 22),
    lineHeight: 1.6,
    letterSpacing: "0em",
    fontWeight: 400,
    fontFamily: "'Public Sans', sans-serif",
  },
} as const;

// ==================== CAPTION STYLE ====================

export const CAPTION_STYLE: TypographyStyle = {
  fontSize: createClamp(11, 13),
  lineHeight: 1.5,
  letterSpacing: "0em",
  fontWeight: 400,
  fontFamily: "'Public Sans', sans-serif",
};

// ==================== LINK STYLE ====================

export const LINK_STYLE = {
  fontFamily: "'Public Sans', sans-serif",
  textDecoration: "none",
  transition: "all 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
} as const;

// ==================== COLOR VARIANTS ====================

// Semantic tokens sourced from tokens.css. Kept in the shape of a value
// map so callers can still do `COLORS[variant]` — the value strings are
// CSS custom-property references, which work inside React inline styles.
export const COLORS: ColorConfig = {
  primary: "var(--ink)",
  secondary: "var(--ink-secondary)",
  tertiary: "var(--ink-tertiary)",
  accent: "var(--brand)",
  accentDark: "var(--brand-dark)",
};

// ==================== BADGE VARIANTS ====================

export const BADGE_VARIANTS = {
  // Brand variants
  default: {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#F0F0F0",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  accent: {
    background: "linear-gradient(135deg, #FFC107, #FF9800)",
    color: "#1a1a1a",
    border: "none",
  },
  subtle: {
    background: "rgba(255, 152, 0, 0.15)",
    color: "#FF9800",
    border: "1px solid rgba(255, 152, 0, 0.3)",
  },
  outline: {
    background: "transparent",
    color: "#FF9800",
    border: "1px solid #FF9800",
  },
  accentDark: {
    background: "rgba(255, 152, 0, 0.2)",
    color: "#e68a00",
    border: "1px solid rgba(255, 152, 0, 0.4)",
  },
  // Skill level variants
  beginner: {
    background: "#40c057",
    color: "#fff",
    border: "none",
  },
  improver: {
    background: "#228be6",
    color: "#fff",
    border: "none",
  },
  intermediate: {
    background: "#FF9800",
    color: "#fff",
    border: "none",
  },
  advanced: {
    background: "#fa5252",
    color: "#fff",
    border: "none",
  },
  // Utility variants
  muted: {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#888",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  dot: {
    background: "transparent",
    color: "#FF9800",
    border: "1px solid rgba(255, 152, 0, 0.3)",
  },
} as const;
